import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import {
  incidentFieldConfiguration,
  incidentInitialValues,
  makeFieldConfigDynamic,
} from "../../../formConfigs/Fleet/incidentFieldConfig";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { OptionType } from "../../../interfaces/Fleet/IGenericFormPopupProps";
import RepairPopup from "../RepairPopup/RepairPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import TruckPopup from "../TruckPopup/TruckPopup";
import TrailerPopup from "../TrailerPopup/TrailerPopup";
import DriverPopup from "../DriverPopup/DriverPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

export const fetchAssets = async (
  selectedAssetType: String,
  token: string | null | undefined,
) => {
  const endpoint =
    selectedAssetType === "Truck" ? "getAllTrucks" : "getAllTrailers";
  const response = await axios.get(`${apiVariable}/${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return response.data.map((item: any) => ({
    label: `${item.unitNumber} | ${item.make} ${item.model} ${item.year}`,
    id: item.id.toString(),
  }));
};

export const fetchAllDrivers = async (token: any) => {
  const allDriversResponse = await axios.get(`${apiVariable}/getAllDrivers`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return allDriversResponse.data.map((driver: any) => ({
    label: `${driver.firstName} ${driver.lastName} | ${driver.phoneNumber}`,
    id: driver.id.toString(),
  }));
};

const IncidentPopup = () => {
  const [open, setOpen] = useState(false);
  const [driverData, setDriverData] = useState<OptionType[]>([]);
  const [selectedAssetType, setSelectedAssetType] = useState<string>("");
  const [selectedIncident, setSelectedIncident] = useState({});
  const [affectedTruck, setAffectedTruck] = useState(null);
  const [affectedTrailer, setAffectedTrailer] = useState(null);
  const [openRepairModal, setOpenRepairModal] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const prevAssetAffected = useRef();
  const prevAssetNumber = useRef();
  const prevDriverId = useRef();

  const {
    data: assetData,
    isError,
    error,
  } = useQuery({
    queryKey: ["assets", selectedAssetType],
    queryFn: () => fetchAssets(selectedAssetType, token),
    enabled: !!selectedAssetType,
  });

  const { data: allDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: !!token,
  });

  useEffect(() => {
    if (allDrivers) {
      setDriverData(allDrivers);
    }
  }, [allDrivers]);

  useEffect(() => {
    if (isError && error) {
      setStatus("error");
      setErrorMessage(
        (error as any).response?.data || "Failed to fetch assets",
      );
    }
  }, [isError, error]);

  const incidentMutation = useMutation({
    mutationFn: (data: any) =>
      axios
        .post(`${apiVariable}/incident`, data, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          setSelectedIncident(response.data);
          if (data.assetAffected === "Truck") {
            setAffectedTruck(data.assetNumber);
          } else {
            setAffectedTrailer(data.assetNumber);
          }
        }),
    onSuccess: () => {
      setOpenRepairModal(true);
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    },
    onError: (error: any) => {
      setOpenRepairModal(false);
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit incident");
    },
  });

  const submitHandler = (data: any) => {
    const requiredFields = [
      "complaint",
      "assetAffected",
      "assetNumber",
      "driverId",
      "receivedBy",
    ];

    const missingFields = requiredFields.filter((field) => !data[field]);

    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all fields. Coordinates are optional.");
      return;
    }

    const coordinates = data.coordinates
      ? data.coordinates.split(",").map((coord: string) => coord.trim())
      : [null, null];
    const [latitude, longitude] = coordinates;

    const submitData = {
      ...data,
      incidentType: "New",
      latitude,
      assetNumber: getId(data.assetNumber),
      driverId: getId(data.driverId),
      longitude,
    };

    delete submitData.coordinates;

    incidentMutation.mutate(submitData);
  };
  const handleFetchAssignedDriver = async (truckId: string) => {
    try {
      const response = await axios.get(
        `${apiVariable}/getAssignedDriverOnTruck/${truckId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const assignedDriver = response.data;

      if (assignedDriver && assignedDriver.id) {
        const formattedAssignedDriver = {
          label: `${assignedDriver.firstName} ${assignedDriver.lastName} | ${assignedDriver.phoneNumber}`,
          id: assignedDriver.id.toString(),
        };
        setDriverData([formattedAssignedDriver]);
      } else {
        setDriverData(allDrivers || []);
      }
    } catch (error) {
      setErrorMessage(
        "No driver is assigned to this truck. Please select a driver manually.",
      );
      setStatus("error");
      setDriverData(allDrivers || []);
    }
  };

  const handleFetchDriverFromTrailer = async (trailerId: string) => {
    try {
      const { data: truck } = await axios.get(
        `${apiVariable}/getTruckFromTrailer/${trailerId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!truck?.id) {
        setDriverData(allDrivers || []);
        return;
      }

      const { data: assignedDriver } = await axios.get(
        `${apiVariable}/getAssignedDriverOnTruck/${truck.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (assignedDriver?.id) {
        setDriverData([
          {
            label: `${assignedDriver.firstName} ${assignedDriver.lastName} | ${assignedDriver.phoneNumber}`,
            id: assignedDriver.id.toString(),
          },
        ]);
      } else {
        setDriverData(allDrivers || []);
      }
    } catch {
      setErrorMessage(
        "No driver is assigned to the truck pulling this trailer. Please select a driver manually.",
      );
      setStatus("error");
      setDriverData(allDrivers || []);
    }
  };

  const onValuesChange = (values: any) => {
    const assetAffected = values.assetAffected;
    const assetNumber = getId(values.assetNumber);
    const driverId = values.driverId;

    const assetAffectedChanged = prevAssetAffected.current !== assetAffected;
    const assetNumberChanged = prevAssetNumber.current !== assetNumber;
    const driverIdChanged = prevDriverId.current !== driverId;

    if (assetAffectedChanged || assetNumberChanged) {
      if (assetAffected === "Truck") {
        if (assetNumber) {
          setDriverData([]);
          handleFetchAssignedDriver(assetNumber);
        } else {
          setDriverData(allDrivers || []);
        }
      } else if (assetAffected === "Trailer") {
        if (assetNumber) {
          setDriverData([]);
          handleFetchDriverFromTrailer(assetNumber);
        } else {
          setDriverData(allDrivers || []);
        }
      } else {
        setDriverData(allDrivers || []);
      }
    }

    if (driverIdChanged && prevDriverId.current && !driverId) {
      setDriverData(allDrivers || []);
    }

    prevAssetAffected.current = assetAffected;
    prevAssetNumber.current = assetNumber;
    prevDriverId.current = driverId;
  };

  const dynamicFields = makeFieldConfigDynamic(
    incidentFieldConfiguration,
    setSelectedAssetType,
    selectedAssetType,
  );

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Create Incident
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={incidentInitialValues}
        title="Incident Form"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        autocompleteData={{ drivers: driverData, assets: assetData }}
        loading={incidentMutation.isPending}
        onValuesChange={onValuesChange}
        fieldComponents={{
          assetNumber:
            selectedAssetType === "Trailer" ? (
              <Box display="flex" justifyContent="center" alignItems="center">
                <TrailerPopup />
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center">
                <TruckPopup />
              </Box>
            ),
          driverId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <DriverPopup />
            </Box>
          ),
        }}
      />
      <RepairPopup
        isOpened={openRepairModal}
        setIsOpened={setOpenRepairModal}
        incident={selectedIncident}
        affectedTruckId={affectedTruck}
        affectedTrailerId={affectedTrailer}
      />
      <StatusSnackbar
        status={status}
        successMessage="Incident added successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default IncidentPopup;
